import React, { createContext, Fragment, useContext, useState } from "react"
import ReactMarkdown from "react-markdown"
import MarkdownHelper from "./MarkdownHelper"
import { replaceWith } from "./replace-with"

const FNREGEX = /{fn.}/gi
const FNREGEX_CAPTURE = /{(fn.)}/gi
const SUPCOMMAREGEX = /{sup,}/gi
const FNDEF_SEPARATOR = '*'
const FNDEF_REGEX = /\{(fn\w+)\}\s*(.*)/

export const SuperscriptContext = createContext()

/** Creates a new footnote indexer mutating the given (or empty) `footnotes` and `currentIndex` references. */
export const getFootnoteIndexThunk = (footnotes = {}, currentIndex = 0) =>
    /** Index a new (unique) key and/or retrieve an existing footnote index for a given key
     * @returns {number}
     **/
    (key, addMissing = true) => {
        if (footnotes.hasOwnProperty(key)) {
            return footnotes[key]
        }
        if (addMissing) {
            const newIndex = currentIndex + 1
            footnotes[key] = newIndex
            currentIndex = newIndex

            return newIndex
        }
        return null
    }

export const SuperscriptProvider = (
    /** @type {{ footnotes: string, children: React.ReactNode }} */
    props
) => {
    // a map defining the index of each footnote, keyed by the footnote key e.g. {fna: 1, fnb: 2}
    // this is intentionally not stateful, because we want to derive the footnote indices sequentially on each render.
    // else, this would cause a state update and the component to re-render itself
    let footnotes = {}
    
    const parsed = parseFootnoteDefinitions(props.footnotes)
    const preIndexed = Object.keys(parsed).reduce((acc, item, index) => {
        acc[item] = index + 1
        return acc
    }, {})

    const getOrAddFootnoteIndex = (key) => {
        if (footnotes.hasOwnProperty(key)) {
            return footnotes[key]
        }
        const index = preIndexed[key]
        footnotes[key] = index
        return index
    }
    
    return (
        <SuperscriptContext.Provider value={{ footnotes, parsed, getOrAddFootnoteIndex }}>
            {props.children}
        </SuperscriptContext.Provider>
    )
}

/**
 * Parses the footnote definitions from the input string, matching their
 * real (unique) footnote index using an available SuperscriptProvider.
 **/
export const parseFootnoteDefinitions = (
    /** @type {string} */
    input,
) => {
    if (!input || typeof input !== 'string' || input.length === 0) {
        return {}
    }
    const parts = input.split(FNDEF_SEPARATOR).filter(Boolean)

    /** @type {{ [key: string]: string }} */
    const map = parts.reduce((acc, part) => {
        const match = part.match(FNDEF_REGEX)
        if (!match) {
            return acc
        }
        
        const key = match[1].trim()
        const value = match[2].trim()

        acc[key] = value
        return acc
    }, {})

    return map
}

function* uniqueIndexer() {
    let index = 0
    while (true) {
        yield index++
    }
}

export const useSuperscript = (
    /** @type {string} Input text including superscript extensions like {fna}{sup,}{fnb} */
    text,
    /** @type {boolean} If true, the superscripts are omitted */
    remove = false,
) => {
    if (typeof text !== 'string') {
        return []
    }

    const superscriptValue = useContext(SuperscriptContext)
    if (!superscriptValue) {
        throw new Error('useSuperscript must be used within a SuperscriptProvider')
    }
    const { getOrAddFootnoteIndex } = superscriptValue
    
    if (remove) {
        return replaceWith(text, [
            {
                rx: SUPCOMMAREGEX,
                fn: () => undefined,
            },
            {
                rx: FNREGEX_CAPTURE,
                fn: () => undefined,
            }
        ]).filter(Boolean).join('')
    }
    
    const indexer = uniqueIndexer()
    return replaceWith(text, [
        {
            rx: SUPCOMMAREGEX,
            fn: (_) => <sup key={indexer.next().value}>,</sup>,
        },
        {
            rx: FNREGEX_CAPTURE,
            fn: (match) => {
                const key = match[1].trim()
                const index = getOrAddFootnoteIndex(key)
                return <sup title={key} key={indexer.next().value}>{index}</sup>
            },
        }
    ])
}

export const WithSuperscript = ({ text, children }) => {
    const parts = useSuperscript(text)
    return parts.map(children)
}

export const MarkdownWithSuperscript = ({ text, markdownProps }) => {
    return (
        <WithSuperscript text={text}>
            {(part, index) =>
                {
                    if (typeof part === 'string') {
                        return (
                                <ReactMarkdown
                                    {...markdownProps}
                                    source={MarkdownHelper.parse(part)}
                                    escapeHtml={false}
                                    key={`${index}`}
                                />
                        )
                    }
                    return part
                }
            }
        </WithSuperscript>
    )
}

const SuperscriptHelper = {

    addSuperscripts: ({...usedCampaignValues}) => {
        const campaignObject = usedCampaignValues

        for (var value in campaignObject) {
            if (campaignObject[value] === null || campaignObject[value] === undefined) {
                delete campaignObject[value];
            }
        }

        const fnArray = Object.values(campaignObject).map(el=>el.match(FNREGEX)).flat().filter(el=>el!==null)
        const uniqueFnValues = [...new Set(fnArray)]

        const uniqueFnValuesObject = {}
        uniqueFnValues.forEach((el, index)=>uniqueFnValuesObject[el]=index+1)


        for(const [key, value] of Object.entries(campaignObject)) {
            campaignObject[key] = value.replace(FNREGEX, (el)=>`<sup>${uniqueFnValuesObject[el]}</sup>`)
        }

        for(const [key, value] of Object.entries(campaignObject)) {
            campaignObject[key] = value.replace(SUPCOMMAREGEX, (el)=>'<sup>,</sup>')
        }

        campaignObject.uniqueFnValues = uniqueFnValues

        return campaignObject
    },

    removeSuperscripts: ({...usedCampaignValues}) => {
        const campaignObject = usedCampaignValues

        for (var value in campaignObject) {
            if (campaignObject[value] === null || campaignObject[value] === undefined) {
                delete campaignObject[value];
            }
        }

        for(const [key, value] of Object.entries(campaignObject)) {
            campaignObject[key] = value.replace(FNREGEX, '')
        }

        return campaignObject
    }
}

export default SuperscriptHelper




// WEBPACK FOOTER //
// ./src/components/utils/SuperscriptHelper.js